<template lang="pug">
    div
        // Header: back arrow + session title on one row. The theme
        // (e.g. "Recorded", "Live") now lives inside the description
        // block in SessionInfo so this header stays compact.
        div.sessionHeader
            back-component.sessionHeader__back(
                :route="routerData"
                :iconOnly="true"
            )
            h1.sessionHeader__title {{ session.title }}

        v-layout(raw wrap v-if="!isLoading")
            v-flex(xs12)
                div.videoWrapper

                    div(id="videoAndChat")
                        div.videoWrapperInner
                            // Video Player. The grey default placeholder
                            // is killed by the unscoped CSS at the bottom
                            // of this file (forces #videoPlayer to black
                            // and hides the WebView's native play overlay)
                            // plus a MutationObserver in onPlayerReady that
                            // sets the session poster on the underlying
                            // <video> element.
                            video-player-component(
                                ref="videoPlayer"
                                id="videoPlayer"
                                class="videoPlayer"
                                v-show="showVideo && successRequest"
                                v-if="posterReady"
                                :options="videoOptions"
                                :onPlayerReady="onPlayerReady"
                                :recorded="recordedVideo"
                            )

                            // Coming Soon
                            div.comingSoonWrapper(v-show="(!showVideo && !showMood) || !successRequest")
                                div.comingSoonBackground
                                    div.comingSoon
                                        div.title {{ $t('sessions.edit.comingSoon') }}

                    session-info(:session="session")

        v-dialog.fixedDialog(
            v-model="showMood"
            persistent
            no-click-animation
        )

            new-mood(v-if="showMood")
</template>

<script>
    import ButtonBack from '~components/ButtonBack';
    import Title from '~components/Title';
    import VideoPlayer from '~components/VideoPlayer';
    import NewMood from '~components/NewMood';
    import VueTypes from 'vue-types';
    import dayjs from 'dayjs';
    import { mapActions, mapGetters } from 'vuex';
    import {
        defineTrackviewOptions,
        PictureInPicture,
        AudioForegroundService,
        isAndroid,
    } from '~services';
    import TrackView from '~services/TrackView';
    import SessionInfo from './SessionInfo';
    import { ids } from './NewMood/tempIds';
    import viewingTimeMixin from '~mixins/viewingTime.mixin';

    const SKIP_MOOD_AFTER_TIMEOUT_LENGTH = 10 * 60 * 1000; // 10 minutes

    export default {
        components: {
            NewMood,
            SessionInfo,
            VideoPlayerComponent: VideoPlayer,
            TitleComponent: Title,
            BackComponent: ButtonBack
        },
        mixins: [viewingTimeMixin],
        props: {
            session: VueTypes.object.def({}),
            duration: VueTypes.number.def(0),
            isLoading: VueTypes.bool.def('false')
        },
        data() {
            return {
                ids,
                player: null,
                showVideo: false,
                successRequest: false,
                timeout: null,
                moodAfterStatus: false,
                endedVideo: false,
                skipMoodAfterTimeout: null
            };
        },
        computed: {
            ...mapGetters('sessions', [
                'showMood',
                'mood',
                'moodBefore',
                'isVoted',
                'isCongratulationDialog',
                'isChallenge'
            ]),
            ...mapGetters('auth', ['user', 'isMember', 'isAdmin', 'isGoldman']),

            recordedVideo() {
                return this.session.type === 'recorded';
            },

            /**
             * Resolved poster URL for this session. Falls back to the same
             * S3-hosted "buildingImage" used by Meditation cards
             * (see components/Meditation/Meditation.vue → coverImage())
             * so the player never shows playerjs's default grey graphic.
             */
            sessionPoster() {
                return (
                    this.session?.poster_name ||
                    this.session?.poster ||
                    'https://s3.us-east-2.amazonaws.com/chillanywhere.com/meditations-cover-images/buildingImage.png'
                );
            },

            /**
             * The player should only mount once the session itself has
             * loaded so the MutationObserver in onPlayerReady can set the
             * poster on the <video> element on the very first paint —
             * otherwise the WebView's native grey overlay flashes.
             */
            posterReady() {
                return !!(this.session && this.session.id);
            },

            /**
             * Get milliseconds that left
             * @returns {number}
             */
            time() {
                let currentDate = dayjs(new Date());
                let startedAt = dayjs(new Date(this.session.startedAt));
                let diffTime = startedAt.diff(currentDate, 'milliseconds');

                if (diffTime > 0) {
                    return diffTime;
                }

                return 0;
            },

            /**
             * Full data for router to back button
             */
            routerData() {
                if (this.duration) {
                    return {
                        name: 'dashboard.sessions',
                        params: {
                            title: 'Mini Meditations',
                            theme: '10mins',
                            duration: 659
                        }
                    };
                }
                if (this.session.theme) {
                    return {
                        name: 'dashboard.sessions',
                        params: {
                            theme: this.session.theme,
                            title: this.sessionTheme
                        }
                    };
                }

                return {
                    name: 'dashboard.sessions'
                };
            },

            /**
             * Options for video player
             * @returns {object}
             */
            videoOptions() {
                this.checkShowVideo();

                const file =
                    this.session?.upstreamChannel?.outputs?.length > 0
                        ? this.session?.upstreamChannel?.outputs[0]?.url
                        : null;

                const options = {
                    file
                };

                // Use the session's cover image as the player poster so the
                // pre-play state shows artwork instead of playerjs's default
                // grey "play" placeholder. Falls back to the shared
                // meditation-card placeholder when the session has none.
                options.poster = this.sessionPoster;

                if (this.session?.startTime)
                    options.start = this.session?.startTime;
                if (this.session?.endTime) options.end = this.session?.endTime;

                if (this.session.awsData.upstreamChannel?.subtitles?.length) {
                    let result = '';
                    for (const [
                        index,
                        subtitle
                    ] of this.session.awsData.upstreamChannel.subtitles.entries()) {
                        const languageTranslation = this.$t(
                            `general.languages.${subtitle.language}`
                        );
                        result += `${
                            index !== 0 ? ',' : ''
                        }[${languageTranslation}]${subtitle.location}`;
                    }
                    options.subtitle = result;
                }

                return options;
            },

            sessionTheme() {
                if (
                    this.session.theme === 'live' &&
                    this.session.type === 'recorded'
                )
                    return 'Recorded';

                if (this.session.theme === 'live') return 'Now';

                let themeTitle;
                this.$t('themes.themes').map(item => {
                    if (item.theme === this.session.theme) {
                        themeTitle = item.title;
                    }
                });

                return themeTitle;
            }
        },
        watch: {
            showMood(val) {
                const el = document.body;

                const trackOptions = defineTrackviewOptions(
                    this.user,
                    this.session
                );

                if (val && !this.moodBefore && !this.isVoted && this.isMember) {
                    TrackView('show-mood-before', trackOptions);
                }

                if (val && this.moodBefore && this.isMember) {
                    TrackView('show-mood-after', trackOptions);
                }

                if (val) {
                    el.classList.add('modalOpen');
                    this.disableScroll();
                } else {
                    el.classList.remove('modalOpen');
                    this.enableScroll();
                }

                if (
                    !this.showMood &&
                    !this.moodAfterStatus &&
                    !self.endedVideo &&
                    this.player
                ) {
                    // The cover is dropped by the playerjs `play` event
                    // listener (see onPlayerReady) — that fires when the
                    // first video frame is actually about to render. If
                    // we dropped it here playerjs would still be loading
                    // the video and the grey placeholder would flash.
                    this.player.api('play');
                    this.player.api('unmute');
                    this.player.api('volume', 0.8);
                }
            }
        },
        mounted() {
            this.updateWatchTimeInterval = setInterval(
                () => this.updateViewSession(),
                40000
            );

            // Subscribe on meditation status change
            if (!this.recordedVideo) {
                this.sockets.subscribe(`session.${this.session.id}`, resp => {
                    if (resp.event && resp.event === 'session_started') {
                        this.showVideo = true;
                    }
                });
            }

            this.checkPlaylist();
        },
        beforeDestroy() {
            // The component is actually being torn down (not just
            // keep-alive deactivation) — stop playback for real and free
            // the store reference.
            if (this.player) {
                this.player.api('pause');
                this.player.api('stop');
            }
            this.detachPlayerFromStore();

            // Tear down the Android foreground service in case the user
            // left the view without an explicit pause/finish (back button,
            // navigation, hardware home). Safe no-op on iOS / web.
            if (isAndroid()) {
                AudioForegroundService.stop();
            }

            // Unsubscribe from listening meditation status change
            this.sockets.unsubscribe(`session.${this.session.id}`);

            if (this.skipMoodAfterTimeout) {
                clearTimeout(this.skipMoodAfterTimeout);
            }
        },
        methods: {
            ...mapActions('sessions', [
                'setShowMood',
                'skipMood',
                'setMoodBefore',
                'saveViewedTime',
                'setBackLink',
                'setCongratulationDialog'
            ]),
            ...mapActions('player', {
                registerPlayerInStore: 'register',
                updatePlayerTime: 'updateTime',
                setPlayerPlaying: 'setPlaying',
                detachPlayerFromStore: 'detach'
            }),

            /**
             * Show mood scale  if user wants leave page
             */
            checkLeaveRoute() {
                const sessionDuration =
                    this.session?.type === 'recorded'
                        ? this.session?.duration / 2
                        : 600;

                if (
                    this.totalSeconds >= sessionDuration &&
                    !this.moodAfterStatus
                ) {
                    this.player.api('stop');
                    this.setShowMood();
                    this.defineBackLink();
                    return false;
                }
                return true;
            },

            /**
             * Check live stream starts
             */
            checkPlaylist() {
                let i = 1;
                if (this.session.type === 'recorded')
                    return (this.successRequest = true);

                const getPlaylist = () => {
                    if (++i < 300) {
                        var http = new XMLHttpRequest();
                        http.open('GET', this.videoOptions.file, false);
                        http.send();
                        if (http.status === 200) {
                            this.successRequest = true;
                        } else {
                            this.timeout = setTimeout(() => {
                                getPlaylist();
                            }, 3000);
                        }
                    }
                };

                getPlaylist();
            },

            checkShowVideo() {
                if (this.session.type === 'recorded')
                    return (this.showVideo = true);

                let currentDate = dayjs(new Date());
                let startedAt = dayjs(new Date(this.session.startedAt));
                let diffTime = startedAt.diff(currentDate, 'seconds');

                this.showVideo = !(diffTime > 0); // (diffTime > 0 && diffTime <= 10 * 60)
            },

            /**
             * Init player
             * @param player
             */
            onPlayerReady(player) {
                let self = this;
                let playerElement;

                // Set player
                this.player = player;

                // Hand the Playerjs instance + session info to the global
                // `player` store so the mini-player at the app shell can
                // drive playback and show "now playing" state after the
                // user navigates away from this route.
                this.registerPlayerInStore({
                    player,
                    sessionInfo: {
                        sessionId: this.session.id,
                        sessionTitle: this.session.title,
                        sessionThumbnail: this.sessionPoster,
                        sessionRoute: this.$route.fullPath,
                        duration: this.session.duration || 0
                    }
                });

                // If no mood set autoplay in player. The cover stays in
                // place until the playerjs `play` event fires below — at
                // that moment the first video frame is about to render
                // and it's safe to reveal the canvas.
                if (!this.showMood && !this.moodAfterStatus) {
                    if (!self.endedVideo) {
                        this.player.api('play');
                        this.player.api('unmute');
                        this.player.api('volume', 0.8);
                    }
                }

                playerElement = document.getElementById('videoPlayer');

                // Force-set the poster + black bg on the underlying HTML5
                // <video> element. Android WebView falls back to its own
                // grey "tap to play" overlay otherwise; setting `poster`
                // gives it our cover image as the native default.
                if (playerElement) {
                    const setPoster = () => {
                        const videoEl = playerElement.querySelector('video');
                        if (videoEl && this.sessionPoster) {
                            videoEl.setAttribute('poster', this.sessionPoster);
                            videoEl.style.backgroundColor = '#000';
                        }
                        // iOS: flag the element for auto Picture-in-Picture
                        // on backgrounding. No-op on other platforms.
                        if (videoEl) PictureInPicture.enableForVideoElement(videoEl);
                    };
                    setPoster();
                    // playerjs may insert the <video> element after init —
                    // observe and re-apply if it appears later.
                    if (typeof MutationObserver !== 'undefined') {
                        const mo = new MutationObserver(setPoster);
                        mo.observe(playerElement, {
                            childList: true,
                            subtree: true,
                        });
                        // Stop observing once playback actually starts.
                        playerElement.addEventListener(
                            'play',
                            () => mo.disconnect(),
                            { once: true }
                        );
                    }
                }

                if (playerElement) {
                    // Play — first frame is about to render.
                    playerElement.addEventListener('play', async () => {
                        this.setPlayerPlaying(true);
                        // Android: promote the app to a foreground media
                        // service so the WebView (and its <video> audio
                        // track) keeps running once the screen locks or
                        // the user backgrounds the app. iOS uses
                        // UIBackgroundModes=audio so this is a no-op.
                        if (isAndroid()) {
                            AudioForegroundService.start({
                                title: this.session.title || 'Meditation playing',
                                body: 'Tap to return to the session',
                            });
                        }
                        await this.saveViewSession();
                        this.initUpdateViewingTime();
                    });

                    // Check start time
                    playerElement.addEventListener('time', event => {
                        if (
                            (self.session.type === 'planned' ||
                                self.session.type === 'live' ||
                                self.session.type === 'recorded-live') &&
                            event.info > 0
                        ) {
                            self.showVideo = true;
                        }
                        if (typeof event.info === 'number') {
                            this.updatePlayerTime({
                                currentTime: event.info,
                                duration: event.duration
                            });
                        }
                    });

                    // Duration event fires once the player knows the total.
                    playerElement.addEventListener('duration', event => {
                        if (typeof event.info === 'number') {
                            this.updatePlayerTime({ duration: event.info });
                        }
                    });

                    // Pause
                    playerElement.addEventListener('pause', () => {
                        this.setPlayerPlaying(false);
                        this.stopTimer();
                        // Release the Android foreground service so the
                        // persistent notification disappears the moment
                        // the user pauses. It is re-started on the next
                        // `play` event above.
                        if (isAndroid()) {
                            AudioForegroundService.stop();
                        }
                    });

                    // Handler when FINISHED video or stream
                    playerElement.addEventListener('finish', () => {
                        this.setPlayerPlaying(false);
                        if (isAndroid()) {
                            AudioForegroundService.stop();
                        }
                        this.endVideo(playerElement);
                    });
                }
            },

            endVideo(playerElement) {
                this.endedVideo = true;

                if (
                    document.fullscreenElement ||
                    document.webkitFullscreenElement ||
                    document.mozFullScreenElement
                ) {
                    document.exitFullscreen();
                }

                if (this.player) this.player.api('exitfullscreen');

                if (
                    this.session.type === 'planned' ||
                    this.session.type === 'live' ||
                    this.session.type === 'recorded' ||
                    this.session.type === 'recorded-live'
                ) {
                    // CHECK mood status, if TRUE then show mood scale
                    if (
                        this.session.todayVote &&
                        this.session.todayVote.rateBefore !== undefined &&
                        this.session.todayVote.rateBefore !== null &&
                        !this.session.todayVote.isSkip &&
                        !this.session.todayVote.isCompleted
                    ) {
                        this.player.api('stop');
                        this.moodAfterStatus = true;
                        // Remove player from DOM
                        playerElement.remove();

                        this.setShowMood();
                        this.skipMoodAfterTimeout = setTimeout(() => {
                            this.skipMoodAfterTimeout = null;
                            this.skipMood();
                        }, SKIP_MOOD_AFTER_TIMEOUT_LENGTH);
                        return;
                    }
                }

                this.$router.push({ name: 'dashboard.themes' });
            },

            preventDefault(e) {
                e.preventDefault();
            },

            disableScroll() {
                document.body.addEventListener(
                    'touchmove',
                    this.preventDefault,
                    {
                        passive: false
                    }
                );
            },

            enableScroll() {
                document.body.removeEventListener(
                    'touchmove',
                    this.preventDefault
                );
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl'
    @import "../../../../assets/style/_mixins.styl"

    .videoWrapper
        position: relative
        border-radius: 10px
        overflow: hidden

    // Session header: back arrow + theme caption + title in one
    // coherent row. Spacing and typography follow Material 3's
    // "center-aligned top app bar" pattern (eyebrow label + 20 px
    // title on one line with the icon).
    .sessionHeader
        display: flex
        align-items: center
        gap: 8px
        padding: 16px 0 8px
        min-height: 64px

    .sessionHeader__back
        flex: 0 0 auto
        // Vuetify v-btn ships with horizontal margins that throw off
        // the icon-title baseline. Pin it to zero here.
        margin: 0 !important

    // Reset the browser's default h1 (2em + margins) so the title
    // shares the same vertical rhythm as the back arrow.
    .sessionHeader__title
        flex: 1 1 auto
        min-width: 0
        font-size: 20px
        font-weight: 500
        line-height: 1.3
        color: #ffffff
        margin: 0
        overflow-wrap: anywhere
        text-transform capitalize
        padding-bottom: 0

    .sessionTheme
        font-size: remify(20px)
        font-weight: 300

    .comingSoonWrapper
        display: flex
        background-color: $theme.black

        .comingSoonBackground
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%
            height: 100%
            min-height: 400px
            margin: 0
            background-image: url('./images/coming-soon.png')
            background-size: cover
            background-repeat: round

            .comingSoon
                text-align: center

                .title
                    font-size: 30px !important
                    font-weight: bold

    .videoWrapperInner
        position: relative
        width: 100%




    #videoAndChat
        display: flex
        width: 100%
        justify-content: center
        align-items: center

    .videoPlayer
        aspect-ratio: 16 / 9
        width: 100%
        height: auto

        &.fullScreenVideo
            // 100vh on Android edge-to-edge (targetSdk 36) extends behind
            // the gesture/3-button nav, so the playerjs control bar at the
            // bottom of the iframe sits behind the system bar. Subtract
            // both insets so the controls stay tappable.
            height: calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))
            aspect-ratio: auto

    @media screen and (min-width: $grid-breakpoints.xl)
        .videoPlayer
            height: 800px

    @media screen and (max-width: $grid-breakpoints.lg - 1)
        .videoPlayer
            height: 387px

    @media screen and (max-width: $grid-breakpoints.md - 1)
        .videoPlayer
            height: 460px

        .comingSoonWrapper
            .comingSoonBackground
                margin: 0

                .comingSoon
                    .title
                        font-size 26px !important

    @media screen and (max-width: $grid-breakpoints.md - 1)
        .videoPlayer
            height: 340px
</style>

<style>
    /*
     * Unscoped overrides for playerjs's internal canvas. Two things
     * cause the grey + giant play button on Android:
     *
     *   1. playerjs paints a grey empty-state inside its own divs.
     *   2. Android WebView's native HTML5 <video> element shows a
     *      built-in grey overlay with a play arrow when it has no
     *      poster and isn't yet playing.
     *
     * Force black on every container/video element AND hide the
     * WebKit media-controls overlay so the WebView's native default
     * never paints. Our Vue cover overlay still draws the actual
     * session poster on top until the first frame renders.
     */
    #videoPlayer,
    #videoPlayer > div,
    #videoPlayer video {
        background-color: #000 !important;
    }
    #videoPlayer video::-webkit-media-controls,
    #videoPlayer video::-webkit-media-controls-panel,
    #videoPlayer video::-webkit-media-controls-overlay-play-button,
    #videoPlayer video::-webkit-media-controls-start-playback-button {
        display: none !important;
        -webkit-appearance: none !important;
    }
</style>
